import { api } from '@goaltree/backend/convex/_generated/api'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { Loader2, UserRoundX } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { AcceptCoachOffer, Can } from '@/components/coaching/accept-coach-offer'
import { coachErrorCode, toastCoachError } from '@/components/coaching/coach-utils'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { clearReturnTo, stashReturnTo } from '@/lib/return-to'
import { useUserMode } from '@/store/user-mode.store'

export const Route = createFileRoute('/c/$token')({
  head: () => ({
    meta: [{ title: 'Coach invite — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

const primaryBtn =
  'flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50'

function RouteComponent() {
  const { t } = useTranslation()
  const { token } = Route.useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const preview = useQuery(api.coaching.previewInvite, { token })
  const offer = useQuery(api.coachOffers.preview, { token })
  const cap = useQuery(api.coachOffers.capState, isAuthenticated ? {} : 'skip')
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const accept = useMutation(api.coaching.acceptInvite)
  const [accepting, setAccepting] = useState(false)

  useEffect(() => {
    if (isAuthenticated) clearReturnTo()
  }, [isAuthenticated])

  function signIn() {
    const returnTo = `/c/${token}`
    stashReturnTo(returnTo)
    navigate({ to: '/auth/login', search: { returnTo } })
  }

  async function handleAccept() {
    setAccepting(true)
    try {
      const { clientId } = await accept({ token })
      openDashboard(clientId)
    } catch (err) {
      if (coachErrorCode(err) === 'clientCap') setUpgradeOpen(true)
      else toastCoachError(err, t)
      setAccepting(false)
    }
  }

  function openDashboard(clientId: string) {
    toast.success(t('coaching.accept.accepted', { name: preview?.client.name ?? '' }))
    useUserMode.getState().setMode('coaching')
    navigate({ to: '/coaching/$clientId', params: { clientId }, replace: true })
  }

  const name = preview?.client.name ?? ''
  const blocked =
    preview &&
    (preview.isSelf
      ? t('coaching.accept.self')
      : preview.state === 'expired'
        ? t('coaching.errors.expired')
        : preview.state === 'used' && !preview.alreadyLinked
          ? t('coaching.errors.used')
          : null)

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="shrink-0 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <img src="/favicon.svg?v=4" alt="Goaltree" className="h-7 w-7 select-none" />
          <span className="text-sm font-semibold tracking-tight">Goaltree</span>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        {preview === undefined || offer === undefined || isLoading ? (
          <div className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8">
            <div className="mx-auto size-16 animate-pulse rounded-full bg-muted/40" />
            <div className="mx-auto mt-4 h-5 w-2/3 animate-pulse rounded bg-muted/40" />
            <div className="mt-6 h-24 animate-pulse rounded-xl bg-muted/30" />
            <div className="mt-6 h-11 animate-pulse rounded-full bg-muted/40" />
          </div>
        ) : offer ? (
          <AcceptCoachOffer token={token} offer={offer} onSignIn={signIn} />
        ) : preview === null ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <UserRoundX className="size-10 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-lg font-bold text-foreground">{t('coaching.accept.invalidTitle')}</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              {t('coaching.accept.invalidDesc')}
            </p>
            <Link
              to="/"
              className="mt-2 text-sm font-semibold text-primary-text underline-offset-4 hover:underline"
            >
              {t('share.goToGoaltree')}
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8"
          >
            <div className="flex items-center gap-2.5">
              <UserAvatar
                image={preview.client.image}
                name={name}
                size={36}
                className="size-9 rounded-full"
              />
              <p className="text-sm text-muted-foreground">
                {t('coaching.accept.invitedYou', { name })}
              </p>
            </div>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-foreground">
              {t('coaching.accept.title', { name })}
            </h1>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {t('coaching.accept.youCan')}
            </p>
            <ul className="mt-2 space-y-2 text-sm">
              <Can>
                {preview.scope === 'all'
                  ? t('coaching.accept.editAll', { name })
                  : t('coaching.accept.editSome', { name, count: preview.goalCount })}
              </Can>
              <Can on={preview.canSeeHabits}>{t('coaching.accept.seeHabits')}</Can>
              <Can on={preview.canSeeCheckins}>{t('coaching.accept.seeCheckins')}</Can>
              <Can>{t('coaching.accept.notes')}</Can>
              <Can on={false}>{t('coaching.accept.cannotComplete')}</Can>
            </ul>
            <div className="mt-6 space-y-2">
              {blocked ? (
                <p className="rounded-xl bg-muted/50 px-3 py-2.5 text-center text-sm font-medium text-muted-foreground">
                  {blocked}
                </p>
              ) : preview.alreadyLinked ? (
                <button
                  type="button"
                  className={primaryBtn}
                  onClick={() => navigate({ to: '/coaching' })}
                >
                  {t('coaching.accept.open')}
                </button>
              ) : isAuthenticated ? (
                <button
                  type="button"
                  className={primaryBtn}
                  disabled={accepting}
                  onClick={handleAccept}
                >
                  {accepting && <Loader2 className="size-4 animate-spin" />}
                  {t('coaching.accept.submit')}
                </button>
              ) : (
                <button type="button" className={primaryBtn} onClick={signIn}>
                  {t('coaching.accept.signIn')}
                </button>
              )}
              <Link
                to="/"
                className="block py-2 text-center text-sm font-medium text-muted-foreground hover:text-foreground"
              >
                {t('coaching.accept.notNow')}
              </Link>
            </div>
          </motion.div>
        )}
      </main>
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        reason="pro"
        title={t('coaching.invite.capTitle')}
        description={t('coaching.invite.capDesc', { count: cap?.limit ?? 1 })}
      />
    </div>
  )
}
